import { devRoutes } from "app/dev/dev.routes";
import { DevLayout } from "app/dev/dev-layout";
import { NativeNavigationProvider } from "@eventr/native/navigation/react";
import { NativeNavigationRouter } from "@eventr/native/navigation/react-router";
import { Fragment, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { routes } from "routing";
import { renderRoutes } from "routing/tree";
import { DevOverlay } from "../../common/devOverlay";
import { ScrollDisabledProvider } from "../../state/scrollState/scrollDisabledContext";
import { ScrollWrapper } from "../../state/scrollState/scrollWrapper";
import { useUserPrefsStore } from "../../state/userPrefsState/userPrefs.store";
import { GlobalRouter } from "../globalRouter/globalRouter";
import { ScrollToTop } from "../layout/scrollToTop";
import { ChatTabLock } from "../layout/tab-bar/chatTabLock";
import { nativeNavigationNavigatorOptions, nativeNavigationReact } from "../nativeNavigation/init";
import { NativeRootController } from "../nativeNavigation/NativeRootController";
import { NativeNavigationViewSetup } from "../nativeNavigation/NativeNavigationViewSetup";
import { NativeLanguageSync } from "../nativeNavigation/NativeLanguageSync";
import { NativeThemeSync } from "../nativeNavigation/NativeThemeSync";
import { isIosNativeNavigation } from "../nativeNavigation/platform";

// Splits the routes into two, one for the dev menu and one for the rest of the app.
// On iOS the root router is NativeNavigationRouter, so each route renders into its own
// native view. Android and web use a plain BrowserRouter.
export const DevRoutesSplit = (props: { children: ReactNode }) => {
  const showDevOverlay = useUserPrefsStore((s) => s.showDevOverlay);

  const inner = (
    <ScrollDisabledProvider>
      <ScrollToTop />
      <GlobalRouter>
        <ScrollWrapper>
          <Routes>
            <Route element={<DevLayout />}>{renderRoutes(devRoutes)}</Route>
            {/* Permanent redirect for emailed/bookmarked links to the old path. */}
            <Route path="/devMenu/*" element={<Navigate to={routes.dev.menu.path} replace />} />
            <Route path="/*" element={props.children} />
          </Routes>
        </ScrollWrapper>
      </GlobalRouter>
    </ScrollDisabledProvider>
  );

  return (
    <Fragment>
      {/* This is for the dev window. should only ever be set for dev and test. never prod... */}
      {showDevOverlay === "dev" && <DevOverlay />}
      {/* Above the router on purpose. Under native navigation everything below is
          portaled once per native view, and four copies of one tab's registration would
          fight over the single store entry. */}
      <ChatTabLock />
      {isIosNativeNavigation() ? (
        <NativeNavigationProvider value={nativeNavigationReact!}>
          <NativeRootController />
          <NativeNavigationViewSetup />
          <NativeThemeSync />
          <NativeLanguageSync />
          <NativeNavigationRouter navigation={nativeNavigationNavigatorOptions}>{inner}</NativeNavigationRouter>
        </NativeNavigationProvider>
      ) : (
        <BrowserRouter>{inner}</BrowserRouter>
      )}
    </Fragment>
  );
};
